// Unified Attendance Service - Maintains backward compatibility by delegating to modular services
import type { ApiResponse } from '@/api/shared';
import type { UIExportFormat } from '@/types/fileExport.types';
import type {
  LC_GetEmployeeAttendanceIn,
  AttendancePage,
  LC_RegisterAttendanceIn,
  LC_RegisterAttendanceOut,
  LC_RegisterManualAttendanceIn,
  LC_RegisterManualAttendanceOut,
  AttendanceDetail,
  LC_GetMonthlyAttendanceIn,
  MonthlyAttendancePage,
  LC_GetAttendanceByRangeIn,
  LC_MonthlyCutoffOut,
  LC_GetMonthlyReportIn,
  MonthlyReportPage,
  MonthlyCutsResult,
  LC_GetManagedAttendanceIn,
  LC_UpdateManagedMarkStatusIn,
  LC_UpdateManagedMarkStatusOut,
  ManagedAttendancePage,
  LC_GetMonthlyCutoffsIn,
} from './types';

import { attendanceListService } from './services/attendanceList.service';
import { attendanceDetailService } from './services/attendanceDetail.service';
import { attendanceRegisterService } from './services/attendanceRegister.service';
import { manualAttendanceRegisterService } from './services/manualAttendanceRegister.service';
import { monthlyAttendanceService } from './services/monthlyAttendance.service';
import { monthlyCutsService } from './services/monthlyCuts.service';
import { attendanceExportService } from './services/attendanceExport.service';
import { managedAttendanceService } from './services/managedAttendance.service';

/**
 * Unified AttendanceService that delegates to modular services.
 * Maintains backward compatibility with existing code that uses attendanceService.
 */
export class AttendanceService {
  // --- Attendance List ---
  async getEmployeeAttendance(
    employeeId: number,
    input: LC_GetEmployeeAttendanceIn,
  ): Promise<ApiResponse<AttendancePage>> {
    return attendanceListService.getEmployeeAttendance(employeeId, input);
  }

  // --- Attendance Detail ---
  async getAttendanceDetail(
    employeeId: number,
    attendanceId: number,
  ): Promise<ApiResponse<AttendanceDetail>> {
    return attendanceDetailService.getAttendanceDetail(employeeId, attendanceId);
  }

  // --- Register Attendance ---
  async register(
    employeeId: number,
    input: LC_RegisterAttendanceIn,
  ): Promise<ApiResponse<LC_RegisterAttendanceOut>> {
    return attendanceRegisterService.register(employeeId, input);
  }

  async registerManual(
    input: LC_RegisterManualAttendanceIn,
  ): Promise<ApiResponse<LC_RegisterManualAttendanceOut>> {
    return manualAttendanceRegisterService.register(input);
  }

  // --- Monthly Attendance ---
  async getMonthlyAttendance(
    input: LC_GetMonthlyAttendanceIn,
  ): Promise<ApiResponse<MonthlyAttendancePage>> {
    return monthlyAttendanceService.getMonthlyAttendance(input);
  }

  async getAttendanceByRange(
    input: LC_GetAttendanceByRangeIn,
  ): Promise<ApiResponse<MonthlyAttendancePage>> {
    return monthlyAttendanceService.getAttendanceByRange(input);
  }

  async getMonthlyReport(
    input: LC_GetMonthlyReportIn,
  ): Promise<ApiResponse<MonthlyReportPage>> {
    return monthlyAttendanceService.getMonthlyReport(input);
  }

  // --- Monthly Cuts ---
  async getMonthlyCutoffs(
    input: LC_GetMonthlyCutoffsIn,
  ): Promise<ApiResponse<LC_MonthlyCutoffOut[]>> {
    return monthlyCutsService.getMonthlyCutoffs(input);
  }

  async getMonthlyCutsNew(): Promise<ApiResponse<MonthlyCutsResult>> {
    return monthlyCutsService.getMonthlyCutsNew();
  }

  // --- Export ---
  async exportMonthlyReport(
    queryType: 'MONTHLY_CUT' | 'DATE_RANGE',
    monthlyCutCode: string | undefined,
    dateFrom: string | undefined,
    dateTo: string | undefined,
    hourFormat?: 'HH:MM:SS' | 'DECIMAL',
    exportFormat?: UIExportFormat,
  ): Promise<ApiResponse<{ blob: Blob; filename: string }>> {
    return attendanceExportService.exportMonthlyReport(
      queryType,
      monthlyCutCode,
      dateFrom,
      dateTo,
      hourFormat,
      exportFormat,
    );
  }

  // --- Managed Attendance ---
  async getManagedAttendance(
    employeeId: number,
    input: LC_GetManagedAttendanceIn,
  ): Promise<ApiResponse<ManagedAttendancePage>> {
    return managedAttendanceService.getManagedAttendance(employeeId, input);
  }

  async updateManagedMarkStatus(
    employeeId: number,
    markId: string,
    input: LC_UpdateManagedMarkStatusIn,
  ): Promise<ApiResponse<LC_UpdateManagedMarkStatusOut>> {
    return managedAttendanceService.updateManagedMarkStatus(employeeId, markId, input);
  }
}

export const attendanceService = new AttendanceService();
